import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, take } from 'rxjs/operators';
import { Observable, tap, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import {
  AddBusinessesToQueue,
  GetBusiness,
  GetBusinesses,
  SnoozeBusinesses
} from 'ng2-app/store/businesses/businesses.action';
import { Business } from 'ng2-app/models/businesses/Business.model';
import { BusinessesService } from 'ng2-app/services/businesses/businesses.service';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { LenderUserState } from '../lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { GetUserQueue } from 'ng2-app/store/queue/queue.actions';

export class BusinessesStateModel {
  businesses: Business[];
  loading: boolean;
  loaded: boolean;
}

@State<BusinessesStateModel>({
  name: 'businesses',
  defaults: {
    businesses: [],
    loading: false,
    loaded: false,
  }
})

@Injectable()
export class BusinessesState {

  @Selector()
  static businesses(state: BusinessesStateModel) {
    return state.businesses;
  }

  @Selector()
  static loading(state: BusinessesStateModel) {
    return state.loading;
  }

  lenderUser$: Observable<LenderUser>;
  user: LenderUser;
  private pusherChannel: any;

  constructor(
    private store: Store,
    private businessesService: BusinessesService,
    private pusherService: PusherService,
  ) {
    this.lenderUser$ = this.store.select(LenderUserState.lenderUser);
    this.lenderUser$.subscribe(user => {
      this.user = user;
    });
  }

  @Action(AddBusinessesToQueue)
  addBusinessesToQueue({patchState}: StateContext<BusinessesStateModel>, {businessIds}: AddBusinessesToQueue) {
    if (businessIds.length <= 0) {
      return;
    }

    return this.businessesService.addBusinessesToQueue(businessIds).pipe(
      catchError(err => {
        patchState({loading: false});
        return throwError(() => err);
      }),
      tap(() => {
        this.store.dispatch(new GetUserQueue());
      }),
    );
  }

  @Action(GetBusinesses)
  async getBusinesses({getState, patchState}: StateContext<BusinessesStateModel>, { refresh }: GetBusinesses) {
    const state = getState();

    if (!refresh && (state.loading || state.loaded)) {
      return;
    }

    if (!refresh) {
      patchState({loading: true});
      await this.pusherSubscribe();
    }

    return this.businessesService.get().pipe(
      catchError(err => {
        patchState({loading: false});
        return throwError(() => err);
      }),
      tap((businesses: Business[]) => {
        patchState({
          loading: false,
          loaded: true,
          businesses: businesses
        });
      }),
    );
  }

  @Action(GetBusiness)
  getBusiness({ getState, patchState }: StateContext<BusinessesStateModel>, { borrowerId }: GetBusiness) {
    return this.businessesService.getBusiness(borrowerId).pipe(
      tap((business: Business) => {
        const state = getState();
        const existingIndex = state.businesses.findIndex(b => b.borrowerId === borrowerId);
        const updatedBusinesses = [...state.businesses];
        if (existingIndex !== -1) {
          updatedBusinesses[existingIndex] = business;
        } else {
          updatedBusinesses.push(business);
        }

        patchState({ businesses: updatedBusinesses });
      })
    );
  }

  @Action(SnoozeBusinesses)
  snoozeBusinesses({patchState}: StateContext<BusinessesStateModel>,  {businessIds}: SnoozeBusinesses) {
    if (businessIds.length <= 0) {
      return;
    }

    return this.businessesService.snooze(businessIds).pipe(
      catchError(err => {
        patchState({loading: false});
        return throwError(() => err);
      }),
      tap(() => {

      }),
    );
  }

  private async pusherSubscribe() {
    if (this.pusherChannel) {
      return;
    }
    const env = environment.environment;
    this.lenderUser$.pipe(take(1)).subscribe(currentUser => {
      this.user = currentUser;
    });
    this.pusherChannel = this.pusherService.subscribeRaw(`private-lender-user-${this.user.id}-${env}-notification-channel`);
    this.pusherChannel.bind_global(this.handlePusherMessage.bind(this));
  }

  private handlePusherMessage(eventName: string, data: any) {
    const relevant = (
      (eventName === 'latestUrgentEvent' && data && data.borrowerId)
    );
    if (!relevant) {
      return;
    }
    this.store.dispatch(new GetBusiness(data.borrowerId));
  }
}
